import { useRef, useState } from "react";
import {
  IonModal,
  IonHeader,
  IonContent,
  IonToolbar,
  IonTitle,
} from "@ionic/react";
import { motion, AnimatePresence } from "framer-motion";
import closeIcon from "../../assets/imgs/close.svg";
import { useSelector } from "react-redux";
import { actions } from "../../Redux/store";
import ChatbotImg from "../../assets/imgs/ai-chat-logo.svg";
import tradeAnalyzerFilter from "../../assets/imgs/trade-analyzer-filter.svg";
import addNnewPlayer from "../../assets/imgs/add-new-player.svg";
import tradeAnalyzerlineIcon from "../../assets/imgs/trade-analyzer-line-icon.svg";
import TradeAnalyzerPlayersCard from "./Componet/TradeAnalyzerPlayersCard";
import { useNavigate } from "react-router-dom";
import { hapticsImpactLight } from "../../lib/utils";
import { SelectLeagueButton } from "../LeagueSync/SelectLeagueButton/SelectLeagueButton";
import { useMixpanel } from "../../Context/MixpanelContext/MixpanelProvider";

// Define Variants type inline since it's no longer exported
type Variants = {
  [key: string]: {
    [key: string]: any;
    transition?: any;
  };
};

// Animation variants for different sections
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25,
      mass: 0.8,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: -20,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
    },
  },
};

const sectionHeaderVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
      duration: 0.6,
    },
  },
};

export default function TradeAnalyzerModal() {
  const modal = useRef<HTMLIonModalElement>(null);
  const isOpen = useSelector(
    (sate: any) => sate.modal.tradeAnalyzerModal?.open,
  );
  const Mixpanel = useMixpanel();
  const navigate = useNavigate();
  const toggle = () => {
    actions.modal.closeTradeAnalyzerModal(null);
  };

  function dismiss() {
    modal.current?.dismiss();
    actions.tradingPlayers.clearAllTradingPlayers(null);
    toggle();
  }

  const openPlayerSearchModal = (section: string) => {
    hapticsImpactLight();
    actions.modal.openPlayerSearchModal({ type: section });
  };

  const tradingAwayPlayers = useSelector(
    (state: any) => state.tradingPlayers.tradingAwayPlayers,
  );
  const tradingForPlayers = useSelector(
    (state: any) => state.tradingPlayers.tradingForPlayers,
  );

  const analyzeNow = () => {
    Mixpanel.trackEvent("trade_analysis_executed", {
      name: "Trade Analysis Executed",
      tradingAwayPlayers: tradingAwayPlayers,
      tradingForPlayers: tradingForPlayers,
    });
    hapticsImpactLight();
    dismiss();
    navigate("/trade-analyze-report", {
      state: {
        tradingAwayPlayers: tradingAwayPlayers,
        tradingForPlayers: tradingForPlayers,
      },
    });
  };

  const openLeagueSettingsModal = () => {
    hapticsImpactLight();
    actions.modal.openLeagueSettingsModal(null);
  };

  const handlePlayerClick = (playerId: string) => {
    hapticsImpactLight();
    actions.modal.openPlayerProfileModal({ playerId: playerId });
  };

  const handleDeleteTradingForPlayer = (playerId: string) => {
    hapticsImpactLight();
    actions.tradingPlayers.removeTradingForPlayer(playerId);
  };

  const handleDeleteTradingAwayPlayer = (playerId: string) => {
    hapticsImpactLight();
    actions.tradingPlayers.removeTradingAwayPlayer(playerId);
  };

  const [isSwapping, setIsSwapping] = useState(false);

  const handleSwapPlayers = () => {
    if (isSwapping) return; // Prevent multiple clicks during animation

    hapticsImpactLight();
    setIsSwapping(true);

    // Start the swap animation, then perform the swap action
    setTimeout(() => {
      actions.tradingPlayers.swapTradingPlayers(null);
      setIsSwapping(false);
    }, 300); // Match the animation duration
  };

  // Check if analyze button should be enabled
  const canAnalyze =
    tradingAwayPlayers.length > 0 && tradingForPlayers.length > 0;

  return (
    <IonModal
      onDidDismiss={dismiss}
      className={`full-modal`}
      isOpen={isOpen}
      backdropDismiss={false}
      ref={modal}
    >
      <>
        <IonHeader class="ion-no-border modal-ion-header">
          <IonToolbar className="custom-toolbar mt-[10px]">
            <div className="  flex justify-between items-center sticky top-0 z-50">
              <div
                onClick={dismiss}
                className="flex items-center justify-center w-[32px] h-[32px] bg-white/10 rounded-full cursor-pointer m-3"
              >
                <img
                  src={closeIcon}
                  alt="close"
                  className="w-[12.4px] h-[12.4px]"
                />
              </div>
              <IonTitle>
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={ChatbotImg}
                    alt="AI Chat"
                    className="mr-2  w-[29.8px] h-[22.7px]"
                  />
                  <span className=" [@media(min-width:375px)]:text-[20px] text-[18px] font-medium text-white">
                    Trade Analyzer
                  </span>
                </div>
              </IonTitle>

              <div onClick={openLeagueSettingsModal} className="p-3">
                <img src={tradeAnalyzerFilter} alt="tradeAnalyzerFilter" />
              </div>
            </div>
            {/* <div className=" flex items-center justify-center">
              <SelectLeagueButton />
            </div> */}
          </IonToolbar>
        </IonHeader>
        <IonContent className="relative">
          <motion.div className="px-[20px] pb-24">
            {/* Trading For Section */}
            <motion.div className="pt-4" variants={sectionHeaderVariants}>
              <motion.p className="text-[20px] text-white font-medium">
                Trading For
              </motion.p>

              <AnimatePresence mode="popLayout">
                {tradingForPlayers && tradingForPlayers.length > 0 && (
                  <motion.div
                    className="grid grid-cols-1 gap-4 mt-4"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    {tradingForPlayers?.map((player: any, index: number) => (
                      <motion.div
                        key={player.id || index}
                        variants={itemVariants}
                        layout
                        layoutId={`trading-for-${player.id}`}
                      >
                        <TradeAnalyzerPlayersCard
                          playerSmallIcon={player.playerHeadshotURL}
                          playerName={player.playerName}
                          playerPosition={player.playerPosition}
                          playerStatus={player.playerTeam}
                          bgColor={player.playerTeamShort}
                          cardBg={false}
                          playerId={player.id}
                          onClick={() =>
                            handlePlayerClick(player?.roto_player_id)
                          }
                          onDelete={() =>
                            handleDeleteTradingForPlayer(player.id)
                          }
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {tradingForPlayers?.length < 3 && (
                <div
                  className="flex flex-row items-center gap-2 mt-6"
                  onClick={() => openPlayerSearchModal("tradingFor")}
                >
                  <img src={addNnewPlayer} alt="addNnewPlayer" />
                  <p className="text-[11px] text-white font-medium">
                    Add New Player
                  </p>
                </div>
              )}
            </motion.div>

            {/* Swap Animation Section */}
            <motion.div className="mt-8">
              <div className="flex items-center w-full">
                <motion.div
                  onClick={handleSwapPlayers}
                  animate={{
                    rotate: isSwapping ? 180 : 0,
                    scale: isSwapping ? 1.1 : 1,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                    duration: 0.3,
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="mr-3 cursor-pointer p-2 rounded-full"
                >
                  <motion.img
                    src={tradeAnalyzerlineIcon}
                    alt="tradeAnalyzerlineIcon"
                    animate={{
                      opacity: isSwapping ? 0.7 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.div>

                <motion.div className="h-[1.25px] bg-white/15 w-full"></motion.div>
              </div>
            </motion.div>

            {/* Trading Away Section */}
            <motion.div className="py-8" variants={sectionHeaderVariants}>
              <motion.p className="text-[20px] text-white font-medium">
                Trading Away
              </motion.p>

              <AnimatePresence mode="popLayout">
                {tradingAwayPlayers && tradingAwayPlayers.length > 0 && (
                  <motion.div
                    className="grid grid-cols-1 gap-4 mt-4"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    {tradingAwayPlayers?.map((player: any, index: number) => (
                      <motion.div
                        key={player.id || index}
                        variants={itemVariants}
                        layout
                        layoutId={`trading-away-${player.id}`}
                      >
                        <TradeAnalyzerPlayersCard
                          playerSmallIcon={player.playerHeadshotURL}
                          playerName={player.playerName}
                          playerPosition={player.playerPosition}
                          playerStatus={player.playerTeam}
                          bgColor={player.playerTeamShort}
                          cardBg={false}
                          playerId={player.id}
                          onClick={() =>
                            handlePlayerClick(player?.roto_player_id)
                          }
                          onDelete={() =>
                            handleDeleteTradingAwayPlayer(player.id)
                          }
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {tradingAwayPlayers?.length < 3 && (
                <div
                  className="flex flex-row items-center gap-2 mt-6"
                  onClick={() => openPlayerSearchModal("tradingAway")}
                >
                  <img src={addNnewPlayer} alt="addNnewPlayer" />
                  <p className="text-[11px] text-white font-medium">
                    Add New Player
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>

          {/* Fixed Button at Bottom */}
          <AnimatePresence>
            {canAnalyze && (
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  duration: 0.5,
                }}
                className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black via-black/90 to-transparent pt-4"
                style={{
                  paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)",
                }}
              >
                <motion.div 
                  className="px-[45px]" 
                  onClick={analyzeNow}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <motion.button 
                    className="h-[48.12px] bg-[#21B8CD] w-full flex justify-center items-center rounded-[50.13px]"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  >
                    <span className="text-black text-[12.03px] font-medium">
                      Analyze Now
                    </span>
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </IonContent>
      </>
    </IonModal>
  );
}
